import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query";

export type FailedRequest = { method: string; route: string };

export const withFailedRequest =
  (
    baseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>,
  ): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> =>
  async (args, api, extraOptions) => {
    const result = await baseQuery(args, api, extraOptions);
    if (!result.error) return result;
    const request: FailedRequest = {
      method: (typeof args === "string" ? "GET" : args.method ?? "GET").toUpperCase(),
      route: routeOf(typeof args === "string" ? args : args.url),
    };
    return { ...result, error: Object.assign({}, result.error, { request }) };
  };

export const failedRequestOf = (error: unknown): FailedRequest | undefined => {
  if (typeof error !== "object" || error === null || !("request" in error)) return undefined;
  const { request } = error as { request: unknown };
  if (typeof request !== "object" || request === null) return undefined;
  const { method, route } = request as Partial<FailedRequest>;
  return typeof method === "string" && typeof route === "string" ? { method, route } : undefined;
};

const ID_SEGMENT = /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|\d+)$/i;

export const routeOf = (url: string): string => {
  const path = url.replace(/^[a-z]+:\/\/[^/]+/i, "").split(/[?#]/)[0];
  const segments = path.split("/").filter(Boolean).map((segment) => (ID_SEGMENT.test(segment) ? "{id}" : segment));
  return `/${segments.join("/")}`;
};
